import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import { Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, Button, TextField, IconButton, InputAdornment, Alert, CircularProgress, Tooltip, Box } from '@mui/material';
import { Visibility, VisibilityOff, ContentCopy, Autorenew } from '@mui/icons-material';
import { updateUserPassword } from '../redux/slices/users';
import { showAlert } from '../redux/slices/alerts';

const MIN_LENGTH = 8;

const CONSONANTS = 'bcdfghjkmnprstvwxz';
const VOWELS = 'aeiou';
const DIGITS = '23456789';

const randomIndex = (max) => {
    const cryptoObj = window.crypto || window.msCrypto;
    if (cryptoObj?.getRandomValues) {
        const buffer = new Uint32Array(1);
        cryptoObj.getRandomValues(buffer);
        return buffer[0] % max;
    }
    return Math.floor(Math.random() * max);
};

const pick = (characters) => characters.charAt(randomIndex(characters.length));

const generateTemporaryPassword = () => {
    const syllable = () => pick(CONSONANTS) + pick(VOWELS);
    return `${syllable()}${syllable()}-${syllable()}${syllable()}-${pick(DIGITS)}${pick(DIGITS)}`;
};

const DialogHelper = styled(DialogContentText)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const FormBlock = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const GenerateRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-start',
    marginBottom: theme.spacing(2),
}));

const NoticeAlert = styled(Alert)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const IssuedPasswordField = styled(TextField)(({ theme }) => ({
    '& .MuiInputBase-input': {
        fontFamily: 'monospace',
        fontSize: theme.typography.h5.fontSize,
        letterSpacing: '0.12em',
        wordBreak: 'break-all',
    },
}));

const SetUserPasswordDialog = ({ open, user, onClose }) => {
    const dispatch = useDispatch();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [issuedPassword, setIssuedPassword] = useState(null);

    const userName = user ? (user.fullName || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email) : 'this user';

    const handleClose = () => {
        if (isSaving) return;
        setPassword('');
        setConfirmPassword('');
        setErrors({});
        setIsVisible(false);
        setIssuedPassword(null);
        onClose();
    };

    const handleGenerate = () => {
        const generated = generateTemporaryPassword();
        setPassword(generated);
        setConfirmPassword(generated);
        setErrors({});
        setIsVisible(true);
    };

    const validate = () => {
        const next = {};
        if (password.trim().length < MIN_LENGTH) {
            next.password = `Password must be at least ${MIN_LENGTH} characters`;
        }
        if (!confirmPassword) {
            next.confirmPassword = 'Please type the password again';
        } else if (password !== confirmPassword) {
            next.confirmPassword = 'The two passwords do not match';
        }
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSubmit = () => {
        if (isSaving || !validate()) {
            return;
        }
        setIsSaving(true);
        const submitted = password.trim();
        dispatch(updateUserPassword({
            userId: user._id,
            password: submitted,
            mustChangePassword: true,
            callback: (response) => {
                setIsSaving(false);
                if (!response) {
                    return;
                }
                setIssuedPassword(submitted);
                setPassword('');
                setConfirmPassword('');
                setErrors({});
            },
        }));
    };

    const handleCopy = () => {
        if (!issuedPassword) return;
        navigator.clipboard?.writeText(issuedPassword);
        dispatch(showAlert({ severity: 'success', message: 'Password copied to clipboard' }));
    };

    if (issuedPassword) {
        return (
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
                <DialogTitle>Password set for {userName}</DialogTitle>
                <DialogContent>
                    <NoticeAlert severity="warning">
                        Read this password out to {userName} now — it cannot be shown again. They will be asked to choose their own password the next time they sign in.
                    </NoticeAlert>
                    <IssuedPasswordField
                        fullWidth
                        label="Temporary password"
                        value={issuedPassword}
                        InputProps={{
                            readOnly: true,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Tooltip title="Copy password">
                                        <IconButton size="small" onClick={handleCopy}>
                                            <ContentCopy fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </InputAdornment>
                            ),
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" color="primary" onClick={handleClose}>
                        Done
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>Set a password for {userName}</DialogTitle>
            <DialogContent>
                <DialogHelper variant="body2">
                    Use this when someone cannot receive email and needs a password read out to them. The user will be asked to choose their own password the next time they sign in.
                </DialogHelper>
                <GenerateRow>
                    <Button size="small" variant="outlined" startIcon={<Autorenew />} onClick={handleGenerate} disabled={isSaving}>
                        Generate a password
                    </Button>
                </GenerateRow>
                <FormBlock>
                    <TextField
                        fullWidth
                        autoFocus
                        label="New password"
                        type={isVisible ? 'text' : 'password'}
                        autoComplete="new-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        error={!!errors.password}
                        helperText={errors.password || `At least ${MIN_LENGTH} characters`}
                        disabled={isSaving}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Tooltip title={isVisible ? 'Hide password' : 'Show password'}>
                                        <IconButton size="small" onClick={() => setIsVisible((prev) => !prev)}>
                                            {isVisible ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                        </IconButton>
                                    </Tooltip>
                                </InputAdornment>
                            ),
                        }}
                    />
                </FormBlock>
                <FormBlock>
                    <TextField
                        fullWidth
                        label="Confirm new password"
                        type={isVisible ? 'text' : 'password'}
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        error={!!errors.confirmPassword}
                        helperText={errors.confirmPassword}
                        disabled={isSaving}
                    />
                </FormBlock>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isSaving}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={isSaving}
                    startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
                >
                    {isSaving ? 'Setting…' : 'Set password'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SetUserPasswordDialog;
